import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import InventorySection from "../../components/admin/InventorySection";
import RoomMap from "../../components/RoomMap";
import RoomCalendar from "../../components/RoomCalendar";
import OccupancyMap from "../../components/OccupancyMap";
import {
  fetchAdminUnit,
  fetchAdminRooms,
  createAdminRoom,
  patchAdminRoom,
  deleteAdminRoom,
  fetchAdminOccupancyRooms,
  fetchAdminTenancies,
  fetchAdminTenant,
  fetchAdminLandlord,
  fetchAdminLandlords,
  fetchAdminOwners,
  fetchAdminProperties,
  fetchAdminPropertyManagers,
  fetchAdminAuditLogs,
  fetchAdminUnitDocuments,
  uploadAdminUnitDocument,
  fetchAdminUnitDocumentDownloadUrl,
  deleteAdminUnitDocument,
  deleteAdminUnit,
  fetchAdminUnitCosts,
  createAdminUnitCost,
  updateAdminUnitCost,
  deleteAdminUnitCost,
  updateAdminUnit,
  fetchAdminTenancyRevenue,
  normalizeUnit,
  normalizeRoom,
} from "../../api/adminData";
import {
  getUnitOccupancyStatus,
  getRoomOccupancyStatus,
  formatOccupancyStatusDe,
  occupancyStatusBadgeTone,
  isLandlordContractLeaseStarted,
  getUnitContractState,
  getTodayIsoForOccupancy,
  parseIsoDate,
  isTenancyActiveByDates,
  isActiveRoom,
} from "../../utils/unitOccupancyStatus";
import { getCoLivingMetrics } from "../../utils/adminUnitCoLivingMetrics";
import {
  getUnitCostsTotal,
  projectRunningCostsBreakdown,
  oneTimeUnitCostBreakdownEntries,
  totalOneTimeUnitCosts,
  sumOneTimeUnitCostsInCalendarMonth,
} from "../../utils/adminUnitRunningCosts";
import {
  aggregateRecurringMonthlyBreakdownRows,
  aggregateOneTimeBreakdownRows,
  aggregateOneTimeTotalFromRowArrays,
} from "../../utils/tenancyRevenueBreakdown";
import { formatAuditLog, auditActorDisplay } from "../../utils/auditDisplay";
import { AdminAuditTimeline, AdminAuditTimelineEntry } from "../../components/admin/AuditLogVisual";
import {
  roundCurrency,
  formatCurrency,
  formatChfOrDash,
  formatChfNetChange,
  formatPercent,
  LEASE_TYPE_LABELS,
  landlordContractStateDerivedTone,
  dashEmpties,
  landlordLeaseTypeLabel,
  landlordLeaseContractStatus,
  landlordLeaseStatusBadgeTone,
  landlordLeaseNotesDisplay,
  calendarDaysUntil,
  formatTenancyMoveIn,
  formatTenancyMoveOut,
  tenancyDisplayStatusLabelDeUnit,
  tenancyMieterTableSortRank,
  tenancyMieterTableStatusMeta,
  TENANT_DEPOSIT_TYPE_LABELS,
  TENANT_DEPOSIT_PROVIDER_LABELS,
  tenantDepositTypeLabel,
  tenantDepositProviderLabel,
  isUuidLike,
  landlordDisplayName,
  propertyManagerDisplayName,
  formatUnitHeaderLocationLine,
  getUnitPageMainTitle,
  formatUnitDocumentDate,
  formatUnitDocumentType,
  UNIT_DOCUMENT_CATEGORY_LABELS,
  formatUnitDocumentCategoryLabel,
  getStatusTone,
  getRoomOccBadgeTone,
  roomDisplayMoveIn,
  roomDisplayMoveOut,
  getActiveOrFutureTenancyForRoom,
  secondaryParticipantLabels,
  roomDisplayTenantName,
  formatDeShort,
  roomCompactFutureSignal,
  mergeUnitWarningsByText,
  buildUnitWarnings,
} from "./unit/unitDetailHelpers";
import { currentBusinessPeriod } from "../../utils/datetime";
import SectionCard from "./unit/SectionCard";
import SmallStatCard from "./unit/SmallStatCard";
import Badge from "./unit/Badge";
import UnitStammdatenSection from "./unit/UnitStammdatenSection";
import UnitHeroBar from "./unit/UnitHeroBar";
import UnitFinanceDetails from "./unit/UnitFinanceDetails";
import UnitCostsSection from "./unit/UnitCostsSection";
import UnitStatsAndWarnings from "./unit/UnitStatsAndWarnings";
import UnitRoomModal from "./unit/UnitRoomModal";
import UnitAssignModal from "./unit/UnitAssignModal";
import { useUnitKpi } from "./hooks/useUnitKpi";
import { DetailTabs, DetailTabPanel } from "@/components/ui/DetailTabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import EntityActivityFeed from "./activity/EntityActivityFeed";
import EntityTasksTab from "./tasks/EntityTasksTab";
import EntityWorkReportsTab from "./tasks/EntityWorkReportsTab";


const LANDLORD_DEPOSIT_TYPE_LABELS = {
  bank: "Bankdepot",
  insurance: "Versicherung",
  cash: "Bareinzahlung",
  none: "Keine",
};

const UNIT_COST_FIXED_SET = new Set([
  "Miete",
  "Nebenkosten",
  "Reinigung",
  "Internet",
]);

const UNIT_DETAIL_TABS = [
  { id: "overview", label: "Übersicht" },
  { id: "rooms", label: "Räume", lazyMount: true },
  { id: "tenants", label: "Mieter" },
  { id: "finance", label: "Finanzen" },
  { id: "inventory", label: "Inventar" },
  { id: "documents", label: "Dokumente" },
  { id: "tasks", label: "Aufgaben", lazyMount: true },
  // D2c — beside Aufgaben; lazyMount so opening a unit does not fetch
  // reports for a tab nobody looked at (same treatment as Aufgaben).
  { id: "reports", label: "Rapporte", lazyMount: true },
  { id: "history", label: "Aktivität" },
];

/**
 * ROOM-ACT-2 — the general room status THIS PAGE prints.
 *
 * Activity outranks occupancy, and only in presentation: a retired room is
 * not free, occupied or reserved — it has left the lettable inventory, and a
 * page that calls it "Frei" invites someone to let it. Nothing about the
 * tenancy truth changes: `getRoomOccupancyStatus` still decides occupancy on
 * its own, every counter and the revenue are untouched, and a running tenancy
 * on a retired room stays fully visible in its own columns.
 *
 * `isActiveRoom` is the only activity truth. NOT `room.status`: the wire
 * coalesces that column to "Frei" even for a room that was stood down
 * (`routes_admin_rooms.py`), so reading it here would invert the answer.
 *
 * WHY THE SENTINEL IS THE GERMAN WORD. The two shared helpers this page's
 * three status badges already use both pass an unknown key straight through:
 * `formatOccupancyStatusDe` returns it verbatim (it has no "inaktiv" branch,
 * and OC4b-1 kept it that way for its eleven consumers), and
 * `getRoomOccBadgeTone` falls through to the neutral `slate` — which is
 * exactly how a retired room should look beside a green "Belegt" and a rose
 * "Frei". So `"Inaktiv"` renders correctly everywhere WITHOUT touching either
 * helper, including inside `UnitRoomModal`, which formats the badge itself.
 * A lowercase key would print lowercase there.
 */
function presentedRoomStatus(room, tenancies) {
  if (!isActiveRoom(room)) return "Inaktiv";
  return tenancies != null ? getRoomOccupancyStatus(room, tenancies) : null;
}

function AdminUnitDetailPage() {
  const { unitId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(true);
  const [auditLogError, setAuditLogError] = useState("");
  const [unitDocuments, setUnitDocuments] = useState([]);
  const [unitDocumentsLoading, setUnitDocumentsLoading] = useState(true);
  const [unitDocumentsError, setUnitDocumentsError] = useState("");
  const [unitDocUploading, setUnitDocUploading] = useState(false);
  const [unitDocUploadError, setUnitDocUploadError] = useState("");
  const [unitDocCategory, setUnitDocCategory] = useState("");
  const unitDocFileInputRef = useRef(null);
  const [unitCosts, setUnitCosts] = useState([]);
  const [costForm, setCostForm] = useState({
    cost_type: "",
    custom_type: "",
    amount_chf: "",
    frequency: "monthly",
  });
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState("");
  const [editingCostId, setEditingCostId] = useState(null);
  const [verwaltungLabel, setVerwaltungLabel] = useState("");
  const [bewirtschafterLabel, setBewirtschafterLabel] = useState("");
  const [linksResolving, setLinksResolving] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignErr, setAssignErr] = useState(null);
  const [assignForm, setAssignForm] = useState({
    property_id: "",
    landlord_id: "",
    property_manager_id: "",
    owner_id: "",
  });
  const [assignLists, setAssignLists] = useState({
    properties: [],
    landlords: [],
    pms: [],
    owners: [],
  });
  const [assignListsLoading, setAssignListsLoading] = useState(false);

  // Pinned once on mount, and BOTH halves from a single reading of the Swiss
  // clock — two separate reads can straddle 31 December midnight and request
  // a KPI period that never existed.
  const [kpiPeriod] = useState(() => currentBusinessPeriod());
  const kpiYear = kpiPeriod.year;
  const kpiMonth = kpiPeriod.month;
  const {
    unitKpiProfit,
    unitKpiOcc,
    unitKpiLoading,
    unitKpiErr,
    reloadUnitKpi,
  } = useUnitKpi(unitId, kpiYear, kpiMonth);
  const [unitTenancyRevenueByTenancyId, setUnitTenancyRevenueByTenancyId] =
    useState({});
  const [unitTenancyRevenueLoading, setUnitTenancyRevenueLoading] =
    useState(false);


  useEffect(() => {
    if (!unitId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchAdminUnit(unitId)
      .then((u) => setUnit(u ? normalizeUnit(u) : null))
      .catch(() => setUnit(null))
      .finally(() => setLoading(false));
  }, [unitId, location.key]);

  useEffect(() => {
    if (!unitId) return;
    setCostError("");
    setCostLoading(true);
    fetchAdminUnitCosts(unitId)
      .then((rows) => setUnitCosts(Array.isArray(rows) ? rows : []))
      .catch((e) => {
        setUnitCosts([]);
        setCostError(
          e.message || "Zusätzliche Kosten konnten nicht geladen werden."
        );
      })
      .finally(() => setCostLoading(false));
  }, [unitId]);

  useEffect(() => {
    if (!unit) {
      setVerwaltungLabel("");
      setBewirtschafterLabel("");
      setLinksResolving(false);
      return;
    }
    const lid = unit.landlord_id;
    const pmid = unit.property_manager_id;
    if (!lid && !pmid) {
      setVerwaltungLabel("");
      setBewirtschafterLabel("");
      setLinksResolving(false);
      return;
    }
    setLinksResolving(true);
    let cancelled = false;
    Promise.all([
      lid ? fetchAdminLandlord(lid) : Promise.resolve(null),
      pmid ? fetchAdminPropertyManagers() : Promise.resolve(null),
    ])
      .then(([ll, pmList]) => {
        if (cancelled) return;
        if (lid) {
          setVerwaltungLabel(ll ? landlordDisplayName(ll) : "—");
        } else {
          setVerwaltungLabel("");
        }
        if (pmid) {
          const p = Array.isArray(pmList) ? pmList.find((x) => String(x.id) === String(pmid)) : null;
          setBewirtschafterLabel(p ? propertyManagerDisplayName(p) : "—");
        } else {
          setBewirtschafterLabel("");
        }
      })
      .catch(() => {
        if (!cancelled) {
          if (lid) setVerwaltungLabel("—");
          if (pmid) setBewirtschafterLabel("—");
        }
      })
      .finally(() => {
        if (!cancelled) setLinksResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [unit]);

  useEffect(() => {
    if (!unitId) return;
    fetchAdminRooms(unitId)
      .then((data) => setRooms(Array.isArray(data) ? data.map(normalizeRoom) : []))
      .catch(() => setRooms([]));
  }, [unitId]);

  const reloadAuditLogs = useCallback(() => {
    if (!unitId) return Promise.resolve();
    setAuditLogLoading(true);
    setAuditLogError("");
    return fetchAdminAuditLogs({ entity_type: "unit", entity_id: unitId })
      .then((data) => setAuditLogs(Array.isArray(data.items) ? data.items : []))
      .catch((e) => {
        setAuditLogError(e.message || "Fehler beim Laden des Verlaufs.");
        setAuditLogs([]);
      })
      .finally(() => setAuditLogLoading(false));
  }, [unitId]);

  useEffect(() => {
    reloadAuditLogs();
  }, [unitId, reloadAuditLogs, location.key]);

  useEffect(() => {
    if (!unitId) {
      setUnitDocumentsLoading(false);
      return;
    }
    setUnitDocumentsLoading(true);
    setUnitDocumentsError("");
    fetchAdminUnitDocuments(unitId)
      .then(setUnitDocuments)
      .catch((e) => {
        setUnitDocumentsError(e.message || "Fehler beim Laden der Dokumente.");
        setUnitDocuments([]);
      })
      .finally(() => setUnitDocumentsLoading(false));
  }, [unitId]);

  const auditResolvers = useMemo(() => {
    const landlordById = {};
    if (unit?.landlord_id && verwaltungLabel && verwaltungLabel !== "—") {
      landlordById[String(unit.landlord_id)] = verwaltungLabel;
    }
    const pmById = {};
    if (unit?.property_manager_id && bewirtschafterLabel && bewirtschafterLabel !== "—") {
      pmById[String(unit.property_manager_id)] = bewirtschafterLabel;
    }
    const propertyById = {};
    if (unit?.property_id) {
      const title = String(unit.property_title || "").trim();
      if (title) propertyById[String(unit.property_id)] = title;
    }
    const ownerById = {};
    if (unit?.owner_id) {
      const title = String(unit.ownerName ?? unit.owner_name ?? "").trim();
      if (title) ownerById[String(unit.owner_id)] = title;
    }
    return { landlordById, pmById, propertyById, ownerById };
  }, [unit, verwaltungLabel, bewirtschafterLabel]);

  const openAssignModal = () => {
    if (!unit) return;
    setAssignErr(null);
    setAssignForm({
      property_id: unit.property_id != null ? String(unit.property_id) : "",
      landlord_id: unit.landlord_id != null ? String(unit.landlord_id) : "",
      property_manager_id:
        unit.property_manager_id != null ? String(unit.property_manager_id) : "",
      owner_id: unit.owner_id != null ? String(unit.owner_id) : "",
    });
    setAssignOpen(true);
    setAssignListsLoading(true);
    Promise.all([
      fetchAdminProperties().catch(() => []),
      fetchAdminLandlords("active").catch(() => []),
      fetchAdminPropertyManagers().catch(() => []),
      fetchAdminOwners()
        .then((r) => (Array.isArray(r.items) ? r.items : []))
        .catch(() => []),
    ])
      .then(([props, landlords, pms, owners]) => {
        const propList = Array.isArray(props) ? props : [];
        const filteredProps = propList.filter((p) => !p.deleted_at);
        filteredProps.sort((a, b) =>
          String(a.title || "").localeCompare(String(b.title || ""), "de")
        );
        const ll = Array.isArray(landlords) ? landlords : [];
        ll.sort((a, b) => {
          const la = `${a.company_name || ""} ${a.contact_name || ""}`.trim();
          const lb = `${b.company_name || ""} ${b.contact_name || ""}`.trim();
          return la.localeCompare(lb, "de");
        });
        const pm = Array.isArray(pms) ? pms : [];
        pm.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "de"));
        const ow = Array.isArray(owners) ? owners : [];
        ow.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "de"));
        setAssignLists({ properties: filteredProps, landlords: ll, pms: pm, owners: ow });
      })
      .finally(() => setAssignListsLoading(false));
  };

  const saveAssignModal = () => {
    if (!unitId) return;
    setAssignSaving(true);
    setAssignErr(null);
    updateAdminUnit(unitId, {
      property_id: assignForm.property_id.trim() || null,
      landlord_id: assignForm.landlord_id.trim() || null,
      property_manager_id: assignForm.property_manager_id.trim() || null,
      owner_id: assignForm.owner_id.trim() || null,
    })
      .then(() => fetchAdminUnit(unitId))
      .then((u) => setUnit(u ? normalizeUnit(u) : null))
      .then(() => reloadAuditLogs())
      .then(() => {
        setAssignOpen(false);
      })
      .catch((e) => {
        setAssignErr(e?.message || "Speichern fehlgeschlagen.");
      })
      .finally(() => setAssignSaving(false));
  };

  const [occupancyRoomsData, setOccupancyRoomsData] = useState(null);
  // LOADING IS TRACKED SO THE MAP CAN TELL IT FROM A FAILURE (OC1 §8).
  // `occupancyRoomsData` is `null` both before the response arrives and after a
  // failed one, and since OC1 the map no longer invents a status from the legacy
  // `Room.status` column — it says "Belegungsdaten nicht verfügbar" instead.
  // Without this flag that honest sentence would flash on every mount. The
  // `loading` prop it feeds has existed on `OccupancyMap` all along; nobody
  // passed it.
  const [occupancyRoomsLoading, setOccupancyRoomsLoading] = useState(true);
  useEffect(() => {
    if (!unitId) return;
    const onDate = getTodayIsoForOccupancy();
    setOccupancyRoomsLoading(true);
    fetchAdminOccupancyRooms({ unit_id: unitId, on_date: onDate })
      .then((data) => setOccupancyRoomsData(data))
      .catch(() => setOccupancyRoomsData(null))
      .finally(() => setOccupancyRoomsLoading(false));
  }, [unitId]);

  const [unitTenancies, setUnitTenancies] = useState(null);
  const [tenantNameMap, setTenantNameMap] = useState({});
  useEffect(() => {
    if (!unitId) return;
    setUnitTenancies(null);
    setTenantNameMap({});
    fetchAdminTenancies({ unit_id: unitId, limit: 200 })
      .then((items) => setUnitTenancies(Array.isArray(items) ? items : []))
      .catch(() => setUnitTenancies([]));
  }, [unitId]);

  useEffect(() => {
    if (!unitTenancies || !unitId) return;
    const uid = String(unitId);
    const idSet = new Set();
    for (const t of unitTenancies.filter((x) => String(x.unit_id || x.unitId) === uid)) {
      const pt = String(t.tenant_id || "").trim();
      if (pt) idSet.add(pt);
      const parts = t.participants;
      if (Array.isArray(parts)) {
        for (const p of parts) {
          const pid = p && String(p.tenant_id || "").trim();
          if (pid) idSet.add(pid);
        }
      }
    }
    const ids = [...idSet].filter((id) => id && id !== "undefined");
    if (ids.length === 0) {
      setTenantNameMap({});
      return;
    }
    let cancelled = false;
    Promise.all(ids.map((id) => fetchAdminTenant(id))).then((rows) => {
      if (cancelled) return;
      const m = {};
      ids.forEach((id, i) => {
        const r = rows[i];
        if (r) {
          m[id] =
            String(r.display_name || r.full_name || "").trim() ||
            `${r.first_name || ""} ${r.last_name || ""}`.trim() ||
            "—";
        }
      });
      setTenantNameMap(m);
    });
    return () => {
      cancelled = true;
    };
  }, [unitTenancies, unitId]);

  const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);
  const [editingRoomId, setEditingRoomId] = useState(null);
  // ROOM-ACT-1b — room activity is its own action, not a sixth field in the
  // room form. `pendingDeactivateRoom` drives the confirmation; the busy id
  // makes a double click send exactly one PATCH.
  const [pendingDeactivateRoom, setPendingDeactivateRoom] = useState(null);
  const [roomActivityBusyId, setRoomActivityBusyId] = useState(null);
  const [roomForm, setRoomForm] = useState({
    roomName: "",
    sizeM2: "",
    priceMonthly: "",
    minimumStayMonths: "3",
    noticePeriodMonths: "3",
  });

  const safeUnit = useMemo(
    () =>
      unit || {
        unitId: "",
        id: "",
        type: "",
        place: "",
        zip: "",
        address: "",
        rooms: 0,
        status: "",
        availableFrom: "",
        occupiedRooms: 0,
      },
    [unit]
  );

  const unitRooms = useMemo(() => {
    return rooms.filter((room) => room.unitId === safeUnit.unitId);
  }, [rooms, safeUnit.unitId]);

  /** Belegung fürs Room-Modal: aus Tenancies, nicht aus manuell gesetztem room.status */
  const roomModalDerivedOcc = useMemo(() => {
    if (!isRoomModalOpen || !editingRoomId) return "frei";
    const room = unitRooms.find((r) => String(r.id) === String(editingRoomId));
    if (!room || unitTenancies == null) return null;
    // Same precedence as the table and the overview (ROOM-ACT-2): one page
    // must not call the same room "Frei" here and "Inaktiv" two cards away.
    const occ = presentedRoomStatus(room, unitTenancies);
    return occ != null ? occ : "frei";
  }, [isRoomModalOpen, editingRoomId, unitRooms, unitTenancies]);

  const activeUnitTenancies = useMemo(() => {
    if (!unitTenancies) return [];
    const today = getTodayIsoForOccupancy();
    const uid = String(safeUnit.unitId || safeUnit.id || "");
    return unitTenancies.filter(
      (t) =>
        String(t.unit_id || t.unitId) === uid &&
        isTenancyActiveByDates(t, today)
    );
  }, [unitTenancies, safeUnit.unitId, safeUnit.id]);

  const unitTenanciesForMieterTable = useMemo(() => {
    if (!unitTenancies) return [];
    const uid = String(safeUnit.unitId || safeUnit.id || "");
    const today = getTodayIsoForOccupancy();
    const rows = unitTenancies.filter(
      (t) => String(t.unit_id || t.unitId) === uid
    );
    return [...rows].sort((a, b) => {
      const ra = tenancyMieterTableSortRank(a, today);
      const rb = tenancyMieterTableSortRank(b, today);
      if (ra !== rb) return ra - rb;
      const da = parseIsoDate(a.move_in_date) || "";
      const db = parseIsoDate(b.move_in_date) || "";
      return db.localeCompare(da);
    });
  }, [unitTenancies, safeUnit.unitId, safeUnit.id]);

  const metrics = useMemo(() => {
    const base = getCoLivingMetrics(safeUnit, rooms, unitTenancies);
    const isApt = safeUnit.type === "Apartment";

    if (unitTenancies === null) {
      const fromOccEarly = unitKpiOcc != null;
      let tr = base.totalRooms;
      let oc = base.occupiedCount;
      let rc = base.reservedCount;
      let fc = base.freeCount;
      if (fromOccEarly && unitKpiOcc.total_rooms != null) {
        tr = unitKpiOcc.total_rooms;
        oc = unitKpiOcc.occupied_rooms ?? base.occupiedCount;
        rc = unitKpiOcc.reserved_rooms ?? base.reservedCount;
        fc = unitKpiOcc.free_rooms ?? base.freeCount;
      }
      const r0 = unitKpiProfit?.revenue ?? null;
      const c0 = unitKpiProfit?.costs ?? null;
      const p0 = unitKpiProfit?.profit ?? null;
      if (isApt) {
        return {
          ...base,
          totalRooms: tr,
          occupiedCount: oc,
          reservedCount: rc,
          freeCount: fc,
          apartmentTenanciesLoaded: false,
          apartmentHasActiveTenancy: null,
          currentRevenue: r0,
          runningCosts: c0,
          currentProfit: p0,
        };
      }
      return {
        ...base,
        totalRooms: tr,
        occupiedCount: oc,
        reservedCount: rc,
        freeCount: fc,
        currentRevenue: r0,
        runningCosts: c0,
        currentProfit: p0,
      };
    }

    const rev = unitKpiProfit?.revenue;
    const costs = unitKpiProfit?.costs;
    const prof = unitKpiProfit?.profit;

    const fromOcc = unitKpiOcc != null;
    let totalRooms = base.totalRooms;
    let occupiedCount = base.occupiedCount;
    let reservedCount = base.reservedCount;
    let freeCount = base.freeCount;
    if (fromOcc && unitKpiOcc.total_rooms != null) {
      totalRooms = unitKpiOcc.total_rooms;
      occupiedCount = unitKpiOcc.occupied_rooms ?? base.occupiedCount;
      reservedCount = unitKpiOcc.reserved_rooms ?? base.reservedCount;
      freeCount = unitKpiOcc.free_rooms ?? base.freeCount;
    }

    const vacancyLoss =
      base.fullRevenue != null && rev != null && Number.isFinite(Number(rev))
        ? Math.max(0, Number(base.fullRevenue) - Number(rev))
        : null;

    if (isApt) {
      const occupied = activeUnitTenancies.length > 0;
      // tenantPriceMonthly is the canonical camelCase mapping from
      // adminData/units.js normalizer. Do NOT reintroduce snake_case
      // fallback under any condition — breaks with the established
      // frontend field convention.
      const aptFull =
        base.fullRevenue != null
          ? base.fullRevenue
          : Number(safeUnit.tenantPriceMonthly ?? 0) > 0
            ? Number(safeUnit.tenantPriceMonthly ?? 0)
            : null;
      const aptVacancy =
        aptFull != null && rev != null && Number.isFinite(Number(rev))
          ? Math.max(0, Number(aptFull) - Number(rev))
          : null;
      return {
        ...base,
        totalRooms,
        occupiedCount,
        reservedCount,
        freeCount,
        currentRevenue: rev ?? null,
        runningCosts: costs ?? null,
        currentProfit: prof ?? null,
        fullRevenue: aptFull,
        vacancyLoss: aptVacancy,
        apartmentTenanciesLoaded: true,
        apartmentHasActiveTenancy: occupied,
      };
    }

    return {
      ...base,
      totalRooms,
      occupiedCount,
      reservedCount,
      freeCount,
      currentRevenue: rev ?? null,
      runningCosts: costs ?? null,
      currentProfit: prof ?? null,
      vacancyLoss,
      apartmentTenanciesLoaded: true,
    };
  }, [
    safeUnit,
    rooms,
    unitTenancies,
    activeUnitTenancies,
    unitKpiProfit,
    unitKpiOcc,
  ]);

  const occupancyRate = useMemo(() => {
    if (unitKpiOcc != null && unitKpiOcc.occupancy_rate != null) {
      return Number(unitKpiOcc.occupancy_rate);
    }
    return metrics.totalRooms > 0
      ? (metrics.occupiedCount / metrics.totalRooms) * 100
      : 0;
  }, [unitKpiOcc, metrics.totalRooms, metrics.occupiedCount]);

  /**
   * The unit row as the occupancy helpers should see it (OC2b).
   *
   * THE CONTRADICTION THIS REMOVES. `GET /api/admin/units/{id}` does not carry
   * the canonical snapshot keys -- only the list wires do -- so this page got
   * its authoritative numbers from `useUnitKpi`'s `GET /api/admin/occupancy?
   * unit_id=...` and fed the KPI tiles from `unitKpiOcc` while the badge beside
   * them still ran the legacy client denominator off the DECLARED `unit.rooms`
   * field. OC2 discovery measured the result: a unit declaring nine rooms with
   * four active ones, all occupied, showed "4 / 4" in the tiles and
   * "Teilbelegt" in the badge. One page, two answers about one unit.
   *
   * SAME TRUTH, NO NEW REQUEST. `unitKpiOcc` is already in hand; this only
   * republishes its counters under the canonical key names the helpers read, so
   * the badge and the tiles are now the same measurement rather than two.
   * `/admin/occupancy?unit_id=` is the UNGATED single-unit path, which is the
   * right one here: Layer 1, a unit's own rooms regardless of its lease window.
   *
   * ABSENT, NOT INVENTED. When the KPI request has not answered the keys stay
   * off the object and the helper falls through to this page's active room rows
   * -- the same population, computed locally. Nothing reconstructs a count from
   * `unit.rooms`.
   */
  const unitWithCanonicalOccupancy = useMemo(() => {
    if (!safeUnit) return safeUnit;
    if (unitKpiOcc == null || typeof unitKpiOcc.total_rooms !== "number") {
      return safeUnit;
    }
    return {
      ...safeUnit,
      total_rooms_snapshot: unitKpiOcc.total_rooms,
      occupied_rooms_snapshot: unitKpiOcc.occupied_rooms,
      reserved_rooms_snapshot: unitKpiOcc.reserved_rooms,
      free_rooms_snapshot: unitKpiOcc.free_rooms,
    };
  }, [safeUnit, unitKpiOcc]);

  const unitWarnings = useMemo(() => {
    // The canonical-occupancy view of the unit, so the warning block and the
    // badge cannot disagree about whether the unit is fully let (OC2b).
    return buildUnitWarnings(
      unitWithCanonicalOccupancy,
      unitRooms,
      metrics,
      unitTenancies
    );
  }, [unitWithCanonicalOccupancy, unitRooms, metrics, unitTenancies]);

  const unitContractState = useMemo(
    () => getUnitContractState(safeUnit),
    [safeUnit]
  );

  const derivedUnitOccupancy = useMemo(
    () => getUnitOccupancyStatus(unitWithCanonicalOccupancy, rooms, unitTenancies),
    [unitWithCanonicalOccupancy, rooms, unitTenancies]
  );

  const nextUnitForecast = useMemo(() => {
    const fullPot = metrics.fullRevenue != null ? metrics.fullRevenue : null;
    const openPot =
      fullPot != null && metrics.currentRevenue != null
        ? Math.max(fullPot - metrics.currentRevenue, 0)
        : null;
    return {
      revenue: metrics.currentRevenue,
      forecast30: null,
      expiringRevenue: null,
      futureBookedRevenue: null,
      netChange: null,
      openPotential: openPot,
      fullPotential: fullPot,
      profit: metrics.currentProfit,
    };
  }, [metrics]);

  const activeTenancyIdsKey = useMemo(
    () =>
      activeUnitTenancies
        .map((t) => String(t.id))
        .filter(Boolean)
        .sort()
        .join(","),
    [activeUnitTenancies]
  );

  useEffect(() => {
    if (!unitId) return;
    const ids = activeTenancyIdsKey
      ? activeTenancyIdsKey.split(",").filter(Boolean)
      : [];
    if (ids.length === 0) {
      setUnitTenancyRevenueByTenancyId({});
      setUnitTenancyRevenueLoading(false);
      return;
    }
    let cancelled = false;
    setUnitTenancyRevenueLoading(true);
    setUnitTenancyRevenueByTenancyId({});
    Promise.all(
      ids.map((id) =>
        fetchAdminTenancyRevenue(id)
          .then((rows) => [id, Array.isArray(rows) ? rows : []])
          .catch(() => [id, []])
      )
    )
      .then((pairs) => {
        if (cancelled) return;
        const next = {};
        for (const [id, rows] of pairs) next[id] = rows;
        setUnitTenancyRevenueByTenancyId(next);
      })
      .finally(() => {
        if (!cancelled) setUnitTenancyRevenueLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [unitId, activeTenancyIdsKey]);

  const unitTenancyRevenueRowArrays = useMemo(() => {
    const ids = activeTenancyIdsKey
      ? activeTenancyIdsKey.split(",").filter(Boolean)
      : [];
    return ids
      .map((id) => unitTenancyRevenueByTenancyId[id])
      .filter((x) => Array.isArray(x));
  }, [activeTenancyIdsKey, unitTenancyRevenueByTenancyId]);

  const unitAggregatedRecurringBreakdown = useMemo(
    () => aggregateRecurringMonthlyBreakdownRows(unitTenancyRevenueRowArrays),
    [unitTenancyRevenueRowArrays]
  );
  const unitAggregatedOneTimeTotal = useMemo(
    () => aggregateOneTimeTotalFromRowArrays(unitTenancyRevenueRowArrays),
    [unitTenancyRevenueRowArrays]
  );
  const unitAggregatedOneTimeBreakdown = useMemo(
    () => aggregateOneTimeBreakdownRows(unitTenancyRevenueRowArrays),
    [unitTenancyRevenueRowArrays]
  );

  const unitNumber =
    safeUnit.unitId && safeUnit.unitId.split("-")[2]
      ? safeUnit.unitId.split("-")[2]
      : "0000";

  const nextNumber = unitRooms.length + 1;
  const nextRoomId = `FAH-R-${unitNumber}-${String(nextNumber).padStart(
    2,
    "0"
  )}`;

  const recurringUnitCosts = useMemo(
    () =>
      Array.isArray(unitCosts)
        ? unitCosts.filter(
            (r) => String(r?.frequency || "monthly").trim().toLowerCase() !== "one_time"
          )
        : [],
    [unitCosts]
  );
  const oneTimeUnitCosts = useMemo(
    () =>
      Array.isArray(unitCosts)
        ? unitCosts.filter(
            (r) => String(r?.frequency || "monthly").trim().toLowerCase() === "one_time"
          )
        : [],
    [unitCosts]
  );
  const unitCostsTotalMonthly = useMemo(
    () => getUnitCostsTotal(recurringUnitCosts),
    [recurringUnitCosts]
  );

  // ── Current-state run-rate: read, never recomputed ───────────────────────
  //
  // TWO COST TRUTHS LIVE ON THIS PAGE AND THESE ARE THE FIRST ONE. The backend
  // contract answers "what does this unit cost in a normal month": recurring
  // `unit_costs` classified into operating buckets, plus the landlord deposit
  // insurance premium, with NO lease proration. `metrics.runningCosts` further
  // down answers a different question -- "what did it cost in THIS month" --
  // and is prorated day-exact. They are meant to differ; nothing here may be
  // wired to the other.
  //
  // The figures are read as they arrive. The finance tab used to re-derive them
  // from the raw rows with no classification, which is how it came to show a
  // different number than the units list for the same unit.
  const currentRunningCostsMonthlyChf =
    unit?.running_costs_monthly_chf ?? null;
  const currentRunningCostsDataQuality =
    unit?.running_costs_data_quality ?? null;
  const currentRunningCostsBreakdown = useMemo(
    () => projectRunningCostsBreakdown(unit?.running_costs_breakdown),
    [unit]
  );
  const oneTimeCostBreakdownDisplay = useMemo(
    () => oneTimeUnitCostBreakdownEntries(unitCosts),
    [unitCosts]
  );
  const oneTimeCostTotalDisplay = useMemo(
    () => totalOneTimeUnitCosts(unitCosts),
    [unitCosts]
  );
  const oneTimeCostsInKpiMonth = useMemo(
    () => sumOneTimeUnitCostsInCalendarMonth(unitCosts, kpiYear, kpiMonth),
    [unitCosts, kpiYear, kpiMonth]
  );
  // FAIL-CLOSED ON AN UNKNOWN RECURRING SHARE. When the run-rate is null the
  // total spend is not partially known, it is unknown: adding one-time costs to
  // a missing recurring figure would publish the one-time sum as the whole, i.e.
  // silently treat "we don't know" as zero. The one-time costs stay visible on
  // their own line either way.
  const totalExpendituresInfoMonth = useMemo(
    () =>
      currentRunningCostsMonthlyChf == null
        ? null
        : currentRunningCostsMonthlyChf + oneTimeCostsInKpiMonth,
    [currentRunningCostsMonthlyChf, oneTimeCostsInKpiMonth]
  );

  const [activeTab, setActiveTab] = useState("overview");

  if (loading) {
    return (
      <div className="min-h-full -m-6 p-6 md:p-8 bg-[#f8fafc] text-[#0f172a] [color-scheme:light] dark:bg-[#07090f] dark:text-[#eef2ff] dark:[color-scheme:dark]">
        <p className="text-[#64748b] dark:text-[#6b7a9a]">Lade Unit…</p>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="min-h-full -m-6 p-6 md:p-8 bg-[#f8fafc] text-[#0f172a] [color-scheme:light] dark:bg-[#07090f] dark:text-[#eef2ff] dark:[color-scheme:dark]">
        <h2 className="text-[22px] font-bold text-[#0f172a] dark:text-[#eef2ff] mb-2">
          Unit nicht gefunden
        </h2>
        <p className="text-[#64748b] dark:text-[#6b7a9a] text-[12px] mb-6">
          Für diese Unit ID konnten keine Daten gefunden werden.
        </p>

        <Link
          to="/admin/apartments"
          className="inline-block bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] text-white font-semibold rounded-[8px] border-none px-5 py-3 transition"
        >
          Zurück zu Units
        </Link>
      </div>
    );
  }

  function handleOpenRoomModal() {
    setEditingRoomId(null);
    setRoomForm({
      roomName: `Zimmer ${unitRooms.length + 1}`,
      sizeM2: "",
      priceMonthly: "",
      minimumStayMonths: "3",
      noticePeriodMonths: "3",
    });
    setIsRoomModalOpen(true);
  }

  function handleOpenEditRoomModal(room) {
    setEditingRoomId(room.id);
    setRoomForm({
      roomName: room.roomName || "",
      sizeM2: room.size_m2 == null ? "" : String(room.size_m2),
      priceMonthly: room.priceMonthly ?? "",
      minimumStayMonths: String(room.minimumStayMonths || 3),
      noticePeriodMonths: String(room.noticePeriodMonths || 3),
    });
    setIsRoomModalOpen(true);
  }

  function handleCloseRoomModal() {
    setIsRoomModalOpen(false);
    setEditingRoomId(null);
    setRoomForm({
      roomName: "",
      sizeM2: "",
      priceMonthly: "",
      minimumStayMonths: "3",
      noticePeriodMonths: "3",
    });
  }

  function handleRoomChange(event) {
    const { name, value } = event.target;
    setRoomForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  async function handleRoomSubmit(event) {
    event.preventDefault();

    // Fläche (m²): empty → null (column is nullable, never coerce to 0).
    // Accept comma decimals (German input) by normalizing to a dot; reject
    // non-numeric / negative. Mirrors the apartments room editor
    // (apartments/useUnitFormModal.js).
    const parsedSizeM2 =
      roomForm.sizeM2 === ""
        ? null
        : Number(String(roomForm.sizeM2).replace(",", "."));
    if (
      parsedSizeM2 !== null &&
      (!Number.isFinite(parsedSizeM2) || parsedSizeM2 < 0)
    ) {
      window.alert("Bitte eine gültige Fläche (m²) eingeben.");
      return;
    }

    // Modal does not expose `status` — omit it from both PATCH and CREATE
    // bodies so the backend keeps the existing room status (PATCH) or
    // applies its column default of "Frei" (CREATE).
    const apiBody = {
      name: roomForm.roomName,
      size_m2: parsedSizeM2,
      price: Number(roomForm.priceMonthly || 0),
      default_minimum_stay_months: Number(roomForm.minimumStayMonths || 3),
      default_notice_period_months: Number(roomForm.noticePeriodMonths || 3),
    };

    try {
      if (editingRoomId !== null) {
        await patchAdminRoom(editingRoomId, apiBody);
      } else {
        await createAdminRoom({ unit_id: unitId, ...apiBody });
      }
      const data = await fetchAdminRooms(unitId);
      setRooms(Array.isArray(data) ? data.map(normalizeRoom) : []);
      await reloadAuditLogs();
      handleCloseRoomModal();
    } catch (err) {
      window.alert(
        String(err?.message ?? err) || "Room konnte nicht gespeichert werden."
      );
    }
  }

  async function handleDeleteRoom(id) {
    const confirmed = window.confirm(
      "Möchtest du diesen Room wirklich löschen?"
    );
    if (!confirmed) return;

    try {
      await deleteAdminRoom(id);
      const data = await fetchAdminRooms(unitId);
      setRooms(Array.isArray(data) ? data.map(normalizeRoom) : []);
      await reloadAuditLogs();
    } catch (err) {
      window.alert(
        String(err?.message ?? err) || "Room konnte nicht gelöscht werden."
      );
    }
  }

  /** ROOM-ACT-1b — retire a room, or bring it back.
   *
   * PESSIMISTIC ON PURPOSE. Nothing in the row changes until the server
   * has accepted: ROOM-ACT-1a refuses a deactivation while a current or
   * future tenancy exists, and a local toggle would briefly show a lie
   * the refetch then has to take back.
   *
   * NO CLIENT-SIDE PRECHECK. The page deliberately does NOT ask whether
   * the room has tenancies — duplicating that rule here would be a second
   * truth that drifts, and it would still race a reservation created
   * between render and click. The backend decides; this surfaces its
   * German answer verbatim (`patchAdminRoom` throws the server `detail`).
   *
   * The payload carries ONLY `is_active`: no form data, no full room
   * object, so this action can never silently rewrite other fields.
   */
  async function handleRoomActivityChange(room, nextActive) {
    const roomId = String(room?.id ?? "");
    if (!roomId || roomActivityBusyId === roomId) return;
    setRoomActivityBusyId(roomId);
    try {
      await patchAdminRoom(roomId, { is_active: nextActive });
      const data = await fetchAdminRooms(unitId);
      setRooms(Array.isArray(data) ? data.map(normalizeRoom) : []);
      await reloadAuditLogs();
    } catch (err) {
      window.alert(
        String(err?.message ?? err) ||
          "Zimmerstatus konnte nicht geändert werden."
      );
    } finally {
      setRoomActivityBusyId(null);
    }
  }

  /** Is the room the confirmation is about the one currently mutating? */
  const pendingRoomBusy =
    !!pendingDeactivateRoom &&
    roomActivityBusyId === String(pendingDeactivateRoom.id);

  async function handleConfirmRoomDeactivate() {
    const room = pendingDeactivateRoom;
    if (!room) return;
    await handleRoomActivityChange(room, false);
    setPendingDeactivateRoom(null);
  }

  function handleUnitDocumentPick() {
    unitDocFileInputRef.current?.click();
  }

  async function handleUnitDocumentSelected(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !unitId) return;
    setUnitDocUploading(true);
    setUnitDocUploadError("");
    try {
      const rec = await uploadAdminUnitDocument(unitId, f, {
        category: unitDocCategory.trim() || undefined,
      });
      setUnitDocuments((prev) => [rec, ...prev]);
      setUnitDocCategory("");
      await reloadAuditLogs();
    } catch (err) {
      setUnitDocUploadError(err.message || "Upload fehlgeschlagen.");
    } finally {
      setUnitDocUploading(false);
    }
  }

  async function handleOpenUnitDocument(docId) {
    try {
      const data = await fetchAdminUnitDocumentDownloadUrl(docId);
      if (data && data.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      window.alert(err.message || "Download konnte nicht gestartet werden.");
    }
  }

  async function handleDeleteUnitDocument(docId) {
    const ok = window.confirm("Dokument wirklich löschen?");
    if (!ok) return;
    try {
      await deleteAdminUnitDocument(docId);
      const items = await fetchAdminUnitDocuments(unitId);
      setUnitDocuments(items);
      await reloadAuditLogs();
    } catch (err) {
      window.alert(err.message || "Löschen fehlgeschlagen.");
    }
  }

  const reloadUnitCosts = () => {
    if (!unitId) return Promise.resolve();
    return fetchAdminUnitCosts(unitId)
      .then((rows) => setUnitCosts(Array.isArray(rows) ? rows : []))
      .catch((err) => {
        console.error("AdminUnitDetailPage: fetchAdminUnitCosts failed", err);
        Sentry.captureException(err);
      });
  };

  const reloadUnitSnapshot = () => {
    if (!unitId) return Promise.resolve();
    return fetchAdminUnit(unitId)
      .then((u) => setUnit(u ? normalizeUnit(u) : null))
      .catch(() => {});
  };

  function parseCostAmountInput(raw) {
    const n = Number(String(raw).replace(",", ".").trim());
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  }

  function resolveBackendCostTypeFromForm(form) {
    if (form.cost_type === "Sonstiges") {
      return String(form.custom_type || "").trim();
    }
    return String(form.cost_type || "").trim();
  }

  async function handleUnitCostSubmit(e) {
    e.preventDefault();
    if (!unitId) return;
    setCostError("");
    if (!costForm.cost_type) {
      setCostError("Bitte Kostenart wählen.");
      return;
    }
    if (costForm.cost_type === "Sonstiges") {
      const t = String(costForm.custom_type || "").trim();
      if (!t) {
        setCostError("Bitte Bezeichnung für „Sonstiges“ eingeben.");
        return;
      }
    }
    const amt = parseCostAmountInput(costForm.amount_chf);
    if (amt == null) {
      setCostError("Bitte einen gültigen Betrag grösser als 0 eingeben.");
      return;
    }
    const freq = String(costForm.frequency || "monthly").trim().toLowerCase() || "monthly";
    if (!["monthly", "yearly", "one_time"].includes(freq)) {
      setCostError("Bitte eine gültige Frequenz wählen.");
      return;
    }
    const cost_type = resolveBackendCostTypeFromForm(costForm);
    if (!cost_type) {
      setCostError("Bitte Kostenart angeben.");
      return;
    }
    setCostLoading(true);
    try {
      if (editingCostId) {
        await updateAdminUnitCost(unitId, editingCostId, {
          cost_type,
          amount_chf: amt,
          frequency: freq,
        });
      } else {
        await createAdminUnitCost(unitId, { cost_type, amount_chf: amt, frequency: freq });
      }
      await reloadUnitCosts();
      await reloadUnitSnapshot();
      await reloadUnitKpi();
      await reloadAuditLogs();
      setCostForm({ cost_type: "", custom_type: "", amount_chf: "", frequency: "monthly" });
      setEditingCostId(null);
    } catch (err) {
      setCostError(err.message || "Speichern fehlgeschlagen.");
    } finally {
      setCostLoading(false);
    }
  }

  function handleUnitCostEdit(row) {
    if (!row || !row.id) return;
    const ct = String(row.cost_type || "");
    const freq = String(row.frequency || "monthly").trim().toLowerCase() || "monthly";
    if (UNIT_COST_FIXED_SET.has(ct)) {
      setCostForm({
        cost_type: ct,
        custom_type: "",
        amount_chf: String(row.amount_chf ?? ""),
        frequency: freq,
      });
    } else {
      setCostForm({
        cost_type: "Sonstiges",
        custom_type: ct,
        amount_chf: String(row.amount_chf ?? ""),
        frequency: freq,
      });
    }
    setEditingCostId(String(row.id));
    setCostError("");
  }

  function handleUnitCostCancel() {
    setCostForm({ cost_type: "", custom_type: "", amount_chf: "", frequency: "monthly" });
    setEditingCostId(null);
    setCostError("");
  }

  async function handleUnitCostDelete(row) {
    if (!unitId || !row?.id) return;
    if (!window.confirm("Diesen Eintrag wirklich löschen?")) return;
    setCostLoading(true);
    setCostError("");
    try {
      await deleteAdminUnitCost(unitId, String(row.id));
      if (editingCostId === String(row.id)) {
        setCostForm({ cost_type: "", custom_type: "", amount_chf: "" });
        setEditingCostId(null);
      }
      await reloadUnitCosts();
      await reloadUnitSnapshot();
      await reloadUnitKpi();
      await reloadAuditLogs();
    } catch (err) {
      setCostError(err.message || "Löschen fehlgeschlagen.");
    } finally {
      setCostLoading(false);
    }
  }

  const landlordDepositTypeKey = String(unit.landlordDepositType || "")
    .trim()
    .toLowerCase();
  const showLandlordDepositNone =
    !landlordDepositTypeKey || landlordDepositTypeKey === "none";
  const landlordDepositKindLabel =
    LANDLORD_DEPOSIT_TYPE_LABELS[landlordDepositTypeKey] ||
    unit.landlordDepositType ||
    "—";
  const landlordLeaseContractStatusStr = landlordLeaseContractStatus(unit);
  const landlordContractDerivedState = landlordLeaseContractStatusStr
    ? null
    : getUnitContractState(unit);
  const leaseContractEnded =
    String(unit.leaseStatus ?? unit.lease_status ?? "").trim() === "ended";

  return (
    <div className="min-h-screen -m-6 p-6 md:p-8 bg-[#f8fafc] text-[#0f172a] [color-scheme:light] dark:bg-[#07090f] dark:text-[#eef2ff] dark:[color-scheme:dark]">
      <div className="max-w-[1800px] mx-auto space-y-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div className={leaseContractEnded ? "opacity-75" : undefined}>
            <p className="text-[12px] font-semibold text-[#fb923c]">
              Unit Intelligence
            </p>
            <h2 className="text-[22px] md:text-[26px] font-bold tracking-tight text-[#0f172a] dark:text-[#eef2ff] mt-2">
              {getUnitPageMainTitle(unit)}
            </h2>
            <p className="text-xs text-[#64748b] dark:text-[#6b7a9a] mt-2 font-mono break-all">
              {unit.id || unit.unitId}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {unitContractState === "expiring_soon" ? (
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400">
                Vertrag endet bald
              </span>
            ) : unitContractState === "expired" ? (
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400">
                Vertrag abgelaufen
              </span>
            ) : unitContractState === "unknown" ? (
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-slate-100 border border-black/10 text-[#64748b] dark:bg-white/[0.05] dark:border-white/[0.08] dark:text-[#6b7a9a]">
                Mietbeginn offen
              </span>
            ) : null}
            {derivedUnitOccupancy == null ? (
              <span className="text-[#64748b] dark:text-[#6b7a9a] text-sm">—</span>
            ) : (
              <Badge tone={occupancyStatusBadgeTone(derivedUnitOccupancy)}>
                {formatOccupancyStatusDe(derivedUnitOccupancy)}
              </Badge>
            )}
            <Link
              to="/admin/apartments"
              className="inline-block border border-black/10 bg-transparent text-[#64748b] dark:border-white/[0.1] dark:text-[#8090b0] px-5 py-3 rounded-[8px] font-medium transition hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
            >
              Zurück
            </Link>
            <button
              type="button"
              onClick={() => {
                if (!unitId) return;
                navigate("/admin/apartments", { state: { editUnitId: String(unitId) } });
              }}
              className="inline-block border border-black/10 bg-transparent text-[#64748b] dark:border-white/[0.1] dark:text-[#8090b0] px-5 py-3 rounded-[8px] font-medium transition hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
            >
              Bearbeiten
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!unitId) return;
                const ok = window.confirm("Möchtest du diese Unit wirklich löschen?");
                if (!ok) return;
                try {
                  await deleteAdminUnit(String(unitId));
                  navigate("/admin/apartments");
                } catch (err) {
                  window.alert(err?.message || "Löschen fehlgeschlagen.");
                }
              }}
              className="inline-block border border-red-500/20 bg-red-500/10 text-[#f87171] px-5 py-3 rounded-[8px] font-medium transition hover:bg-red-500/15"
            >
              Löschen
            </button>
          </div>
        </div>

        <DetailTabs
          tabs={UNIT_DETAIL_TABS}
          value={activeTab}
          onValueChange={setActiveTab}
        >

          <DetailTabPanel tabId="overview">
            <div className="space-y-6">
              <UnitHeroBar
                metrics={metrics}
                occupancyRate={occupancyRate}
                unitKpiErr={unitKpiErr}
                unitKpiLoading={unitKpiLoading}
              />

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <UnitStammdatenSection
                  unit={unit}
                  linksResolving={linksResolving}
                  verwaltungLabel={verwaltungLabel}
                  bewirtschafterLabel={bewirtschafterLabel}
                  landlordLeaseContractStatusStr={landlordLeaseContractStatusStr}
                  landlordContractDerivedState={landlordContractDerivedState}
                  onOpenAssignModal={openAssignModal}
                />

                <div className="space-y-6">
                  <SmallStatCard
                    label="Kosten im Monat"
                    value={formatChfOrDash(metrics.runningCosts)}
                    hint={`Kosten für ${String(kpiMonth).padStart(2, "0")}/${kpiYear}, tageweise auf die Mietdauer umgerechnet.`}
                    accent="slate"
                  />
                  <SmallStatCard
                    label="Leerstand"
                    value={formatChfOrDash(metrics.vacancyLoss)}
                    hint="Entgangener Umsatz: Potenzial minus aktueller Umsatz"
                    accent="rose"
                  />
                </div>
              </div>

              <UnitStatsAndWarnings unitWarnings={unitWarnings} />
            </div>
          </DetailTabPanel>

          <DetailTabPanel tabId="rooms">
            <div className="space-y-6">
              {unit.type === "Co-Living" && (
                <SectionCard
                  title="Rooms"
                  subtitle="Echte Room-Verwaltung für diese Co-Living Unit"
                  rightSlot={
                    <button
                      onClick={handleOpenRoomModal}
                      className="bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] text-white font-semibold rounded-[8px] border-none px-4 py-2 transition"
                    >
                      + Room hinzufügen
                    </button>
                  }
                >
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-100 dark:bg-[#111520] text-[9px] font-bold uppercase tracking-[.8px] text-[#64748b] dark:text-[#6b7a9a] text-sm">
                          <th className="py-3 pr-4">Room ID</th>
                          <th className="py-3 pr-4">Zimmer</th>
                          <th className="py-3 pr-4">Status</th>
                          <th className="py-3 pr-4">Mieter</th>
                          <th className="py-3 pr-4">Geplanter Mietpreis</th>
                          <th className="py-3 pr-4">Einzug</th>
                          <th className="py-3 pr-4">Frei ab</th>
                          <th className="py-3 pr-4">Min. Dauer</th>
                          <th className="py-3 pr-4">Kündigung</th>
                          <th className="py-3 pr-4">Aktionen</th>
                        </tr>
                      </thead>

                      <tbody>
                        {unitRooms.map((room) => {
                          const roomOcc = presentedRoomStatus(
                            room,
                            unitTenancies
                          );
                          const tnRoom =
                            unitTenancies != null
                              ? getActiveOrFutureTenancyForRoom(room, unitTenancies)
                              : null;
                          const rTenant = roomDisplayTenantName(
                            room,
                            unitTenancies,
                            tenantNameMap
                          );
                          const secRoom = secondaryParticipantLabels(tnRoom, tenantNameMap);
                          const rIn = roomDisplayMoveIn(room, unitTenancies);
                          const rOut = roomDisplayMoveOut(room, unitTenancies);
                          const futureSig = roomCompactFutureSignal(
                            room,
                            unitTenancies
                          );
                          return (
                          <tr
                            key={room.id}
                            className="border-b border-black/10 dark:border-white/[0.05] text-[#0f172a] dark:text-[#eef2ff]"
                          >
                            <td className="py-4 pr-4 font-semibold text-emerald-600 dark:text-emerald-400">
                              {room.roomId}
                            </td>
                            <td className="py-4 pr-4">
                              <div>{room.roomName}</div>
                              {futureSig ? (
                                <div className="text-xs text-[#6b7a9a] mt-0.5">
                                  {futureSig}
                                </div>
                              ) : null}
                            </td>
                            <td className="py-4 pr-4">
                              <Badge tone={getRoomOccBadgeTone(roomOcc)}>
                                {roomOcc != null
                                  ? formatOccupancyStatusDe(roomOcc)
                                  : "—"}
                              </Badge>
                            </td>
                            <td className="py-4 pr-4">
                              <div className="space-y-0.5">
                                <span>{rTenant}</span>
                                {secRoom.map((s) => (
                                  <div
                                    key={s.tenantId}
                                    className="text-xs text-[#6b7a9a]"
                                  >
                                    + {s.label} ({s.roleDe})
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="py-4 pr-4">
                              {formatChfOrDash(room.priceMonthly)}
                            </td>
                            <td className="py-4 pr-4">{rIn || "-"}</td>
                            <td className="py-4 pr-4">{rOut || "-"}</td>
                            <td className="py-4 pr-4">
                              {room.minimumStayMonths || 3} M
                            </td>
                            <td className="py-4 pr-4">
                              {room.noticePeriodMonths || 3} M
                            </td>
                            <td className="py-4 pr-4">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleOpenEditRoomModal(room)}
                                  className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/[0.1] text-sm hover:bg-slate-100 dark:hover:bg-[#111520]"
                                >
                                  Bearbeiten
                                </button>
                                {/* ROOM-ACT-1b. `isActiveRoom`, never
                                    `room.status`: the wire sends "Frei" even
                                    for a retired room, so the stored status
                                    cannot decide this. */}
                                {isActiveRoom(room) ? (
                                  <button
                                    type="button"
                                    onClick={() => setPendingDeactivateRoom(room)}
                                    disabled={roomActivityBusyId === String(room.id)}
                                    className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/[0.1] text-sm hover:bg-slate-100 dark:hover:bg-[#111520] disabled:opacity-50 disabled:pointer-events-none"
                                  >
                                    Deaktivieren
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleRoomActivityChange(room, true)}
                                    disabled={roomActivityBusyId === String(room.id)}
                                    className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/[0.1] text-sm hover:bg-slate-100 dark:hover:bg-[#111520] disabled:opacity-50 disabled:pointer-events-none"
                                  >
                                    Reaktivieren
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteRoom(room.id)}
                                  className="px-3 py-2 rounded-[8px] border border-red-500/20 bg-red-500/10 text-[#f87171] text-sm hover:bg-red-500/15"
                                >
                                  Löschen
                                </button>
                              </div>
                            </td>
                          </tr>
                          );
                        })}

                        {unitRooms.length === 0 && (
                          <tr>
                            <td
                              colSpan="10"
                              className="py-8 text-center text-[#6b7a9a]"
                            >
                              Noch keine Rooms vorhanden.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              )}

              <SectionCard
                title="Room Status Übersicht"
                subtitle="Operativ pro Zimmer (heute); nicht identisch mit dem Finanz-KPI-Monat oben."
              >
                <div className="space-y-3">
                  {unitRooms.map((room) => {
                    const roomOcc = presentedRoomStatus(room, unitTenancies);
                    const tnRoom =
                      unitTenancies != null
                        ? getActiveOrFutureTenancyForRoom(room, unitTenancies)
                        : null;
                    const rn = roomDisplayTenantName(
                      room,
                      unitTenancies,
                      tenantNameMap
                    );
                    const secRoom = secondaryParticipantLabels(tnRoom, tenantNameMap);
                    const rmi = roomDisplayMoveIn(room, unitTenancies);
                    const futureSig = roomCompactFutureSignal(room, unitTenancies);
                    return (
                    <div
                      key={room.id}
                      className="rounded-2xl border border-black/10 bg-slate-100 dark:border-white/[0.07] dark:bg-[#111520] p-4 flex items-center justify-between gap-4"
                    >
                      <div>
                        <p className="font-semibold text-[#eef2ff]">
                          {room.roomName}
                        </p>
                        <p className="text-sm text-[#6b7a9a] mt-1">
                          Soll {formatChfOrDash(room.priceMonthly)}
                          {rn !== "—" ? ` · ${rn}` : ""}
                          {rmi
                            ? ` · Einzug ${rmi}`
                            : ` · Kein Einzug erfasst`}
                        </p>
                        {secRoom.map((s) => (
                          <p
                            key={s.tenantId}
                            className="text-xs text-[#6b7a9a] mt-1 pl-0"
                          >
                            + {s.label} ({s.roleDe})
                          </p>
                        ))}
                        {futureSig ? (
                          <p className="text-xs text-[#6b7a9a] mt-1">{futureSig}</p>
                        ) : null}
                      </div>

                      <Badge tone={getRoomOccBadgeTone(roomOcc)}>
                        {roomOcc != null ? formatOccupancyStatusDe(roomOcc) : "—"}
                      </Badge>
                    </div>
                    );
                  })}

                  {unitRooms.length === 0 && (
                    <p className="text-[10px] text-[#6b7a9a]">
                      Noch keine Rooms vorhanden.
                    </p>
                  )}
                </div>
              </SectionCard>

              {unit.type === "Co-Living" && (
                <SectionCard
                  title="Raumstatus (Belegung)"
                  subtitle="Belegt (grün), Reserviert (gelb), Frei (rot) – aus Tenancies"
                >
                  <OccupancyMap
                    unit={unit}
                    rooms={unitRooms}
                    occupancyData={occupancyRoomsData}
                    loading={occupancyRoomsLoading}
                  />
                </SectionCard>
              )}

              {unit.type === "Co-Living" && (
                <SectionCard
                  title="Room Map"
                  subtitle="Visuelle Übersicht aller Rooms dieser Unit"
                >
                  <RoomMap unit={unit} rooms={unitRooms} tenancies={unitTenancies} />
                </SectionCard>
              )}

              {unit.type === "Co-Living" && (
                <SectionCard
                  title="Belegungskalender"
                  subtitle="Monatskalender nur für diese Unit"
                >
                  <RoomCalendar unit={unit} rooms={unitRooms} tenancies={unitTenancies} />
                </SectionCard>
              )}
            </div>
          </DetailTabPanel>

          <DetailTabPanel tabId="tenants">
            <SectionCard
              title="Mieter"
              subtitle="Alle Mietverhältnisse dieser Unit (aktiv, geplant/reserviert, beendet). Sortierung: zuerst Aktive, dann Geplant/Reserviert, dann Historie. Einnahmen / Monat aus TenancyRevenue (Backend, Monatsäquivalent)."
            >
              {unitTenancies === null ? (
                <p className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">Lade Mietverhältnisse …</p>
              ) : unitTenanciesForMieterTable.length === 0 ? (
                <p className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                  Keine Mietverhältnisse für diese Unit erfasst.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-[#0f172a] dark:text-[#eef2ff]">
                    <thead>
                      <tr className="bg-slate-100 dark:bg-[#111520] text-[9px] font-bold uppercase tracking-[.8px] text-[#64748b] dark:text-[#6b7a9a]">
                        <th className="py-2 pr-4 font-medium">Name</th>
                        <th className="py-2 pr-4 font-medium">Status</th>
                        <th className="py-2 pr-4 font-medium text-right">
                          Einnahmen / Monat
                        </th>
                        <th className="py-2 pr-4 font-medium">Kautionsart</th>
                        <th className="py-2 pr-4 font-medium text-right">Kautionsbetrag</th>
                        <th className="py-2 pr-4 font-medium">Anbieter</th>
                        <th className="py-2 pr-4 font-medium">Mietbeginn</th>
                        <th className="py-2 pr-4 font-medium">Mietende</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unitTenanciesForMieterTable.map((tn) => {
                        const tdt = String(tn.tenant_deposit_type || "").toLowerCase();
                        const statusMeta = tenancyMieterTableStatusMeta(tn);
                        const secMieter = secondaryParticipantLabels(tn, tenantNameMap);
                        return (
                        <tr key={String(tn.id)} className="border-b border-black/10 dark:border-white/[0.05]">
                            <td className="py-2 pr-4 font-medium">
                              <div className="space-y-0.5">
                                <Link
                                  to={`/admin/tenants/${String(tn.tenant_id)}`}
                                  className="text-sky-700 hover:underline dark:text-sky-400 dark:hover:text-sky-300"
                                >
                                  {tenantNameMap[String(tn.tenant_id)] || "…"}
                                </Link>
                                {secMieter.map((s) => (
                                  <div
                                    key={s.tenantId}
                                    className="text-[12px] font-normal text-[#64748b] dark:text-[#6b7a9a]"
                                  >
                                    <Link
                                      to={`/admin/tenants/${s.tenantId}`}
                                      className="text-sky-700/90 hover:underline dark:text-sky-400/90"
                                    >
                                      + {s.label} ({s.roleDe})
                                    </Link>
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="py-2 pr-4">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <Badge tone={statusMeta.tone}>{statusMeta.label}</Badge>
                                {tn.has_rent_revenue === false ? (
                                  <span
                                    title="Keine Mieteinnahme hinterlegt — Rechnung wird übersprungen."
                                    className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
                                  >
                                    ⚠ Keine Miete
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className="py-2 pr-4 text-right">
                              {formatChfOrDash(tn.monthly_revenue_equivalent)}
                            </td>
                            <td className="py-2 pr-4 text-[#64748b] dark:text-[#6b7a9a]">
                              {tenantDepositTypeLabel(tn.tenant_deposit_type)}
                            </td>
                            <td className="py-2 pr-4 text-right text-[#64748b] dark:text-[#6b7a9a]">
                              {formatChfOrDash(tn.tenant_deposit_amount)}
                            </td>
                            <td className="py-2 pr-4 text-[#64748b] dark:text-[#6b7a9a]">
                              {tdt === "insurance"
                                ? tenantDepositProviderLabel(tn.tenant_deposit_provider)
                                : "—"}
                            </td>
                            <td className="py-2 pr-4 text-[#64748b] dark:text-[#6b7a9a]">
                              {formatTenancyMoveIn(tn.move_in_date)}
                            </td>
                            <td className="py-2 pr-4 text-[#64748b] dark:text-[#6b7a9a] font-medium">
                              {formatTenancyMoveOut(
                                tn.display_end_date != null && String(tn.display_end_date).trim() !== ""
                                  ? tn.display_end_date
                                  : tn.move_out_date
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          </DetailTabPanel>

          <DetailTabPanel tabId="finance">
            <div className="space-y-6">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <SectionCard
                  title="Forecast für diese Unit"
                  subtitle="Echte KPIs wie in der Finanzübersicht; Kurzprognosen hier nicht angebunden"
                >
                  <div className="grid grid-cols-1 gap-4">
                    <SmallStatCard
                      label="Aktueller Umsatz"
                      value={formatChfOrDash(nextUnitForecast.revenue)}
                      hint={`Berechnet aus tatsächlichen Einnahmen (Backend KPI). Zeitraum: ${String(kpiMonth).padStart(2, "0")}/${kpiYear}.`}
                      accent="blue"
                    />
                    <SmallStatCard
                      label="Prognose (geschätzt) · 30 Tage"
                      value={
                        nextUnitForecast.forecast30 != null
                          ? formatChfOrDash(nextUnitForecast.forecast30)
                          : "—"
                      }
                      hint={
                        nextUnitForecast.forecast30 != null
                          ? "Schätzung; kein Backend KPI"
                          : "Noch nicht aktiviert"
                      }
                      accent="slate"
                      valueTone="muted"
                    />
                    <SmallStatCard
                      label="Prognose (geschätzt) · Wegfall"
                      value={
                        nextUnitForecast.expiringRevenue != null
                          ? formatChfOrDash(nextUnitForecast.expiringRevenue)
                          : "—"
                      }
                      hint={
                        nextUnitForecast.expiringRevenue != null
                          ? "Schätzung; kein Backend KPI"
                          : "Noch nicht aktiviert"
                      }
                      accent="slate"
                      valueTone="muted"
                    />
                    <SmallStatCard
                      label="Prognose (geschätzt) · Neu geplant"
                      value={
                        nextUnitForecast.futureBookedRevenue != null
                          ? formatChfOrDash(nextUnitForecast.futureBookedRevenue)
                          : "—"
                      }
                      hint={
                        nextUnitForecast.futureBookedRevenue != null
                          ? "Schätzung; kein Backend KPI"
                          : "Noch nicht aktiviert"
                      }
                      accent="slate"
                      valueTone="muted"
                    />
                    <SmallStatCard
                      label="Prognose (geschätzt) · Veränderung"
                      value={
                        nextUnitForecast.netChange != null
                          ? formatChfNetChange(nextUnitForecast.netChange)
                          : "—"
                      }
                      hint={
                        nextUnitForecast.netChange != null
                          ? "Schätzung; kein Backend KPI"
                          : "Noch nicht aktiviert"
                      }
                      accent="slate"
                      valueTone="muted"
                    />
                    <SmallStatCard
                      label="Gewinn aktuell"
                      value={formatChfOrDash(nextUnitForecast.profit)}
                      hint="Umsatz minus Kosten (Backend berechnet)"
                      accent="green"
                    />
                  </div>
                </SectionCard>

                <SectionCard
                  title="Break-Even"
                  subtitle={`Deckt diese Unit ihre Kosten für ${String(kpiMonth).padStart(2, "0")}/${kpiYear}?`}
                >
                  <div className="grid grid-cols-1 gap-4">
                    <SmallStatCard
                      label="Aktueller Umsatz"
                      value={formatChfOrDash(metrics.currentRevenue)}
                      hint={`Berechnet aus tatsächlichen Einnahmen (Backend KPI). Zeitraum: ${String(kpiMonth).padStart(2, "0")}/${kpiYear}.`}
                      accent="blue"
                    />
                    <SmallStatCard
                      label="Break-Even"
                      value={formatChfOrDash(metrics.runningCosts)}
                      hint={`Erforderlicher Umsatz zur Deckung der Kosten für ${String(kpiMonth).padStart(2, "0")}/${kpiYear}.`}
                      accent="slate"
                    />
                    <SmallStatCard
                      label="Differenz"
                      value={formatChfOrDash(metrics.currentProfit)}
                      hint={
                        metrics.currentProfit != null
                          ? metrics.currentProfit >= 0
                            ? "Über Break-Even (Backend-Gewinn)"
                            : "Unter Break-Even (Backend-Gewinn)"
                          : "Keine Daten vorhanden"
                      }
                      accent={
                        metrics.currentProfit != null
                          ? metrics.currentProfit >= 0
                            ? "green"
                            : "rose"
                          : "slate"
                      }
                    />
                  </div>
                </SectionCard>
              </div>

              <UnitFinanceDetails
                kpiYear={kpiYear}
                kpiMonth={kpiMonth}
                unitTenancyRevenueLoading={unitTenancyRevenueLoading}
                activeUnitTenancies={activeUnitTenancies}
                unitAggregatedRecurringBreakdown={unitAggregatedRecurringBreakdown}
                unitAggregatedOneTimeTotal={unitAggregatedOneTimeTotal}
                unitAggregatedOneTimeBreakdown={unitAggregatedOneTimeBreakdown}
                totalExpendituresInfoMonth={totalExpendituresInfoMonth}
                currentRunningCostsMonthlyChf={currentRunningCostsMonthlyChf}
                currentRunningCostsBreakdown={currentRunningCostsBreakdown}
                currentRunningCostsDataQuality={currentRunningCostsDataQuality}
                oneTimeCostTotalDisplay={oneTimeCostTotalDisplay}
                oneTimeCostBreakdownDisplay={oneTimeCostBreakdownDisplay}
              />

              <UnitCostsSection
                recurringUnitCosts={recurringUnitCosts}
                oneTimeUnitCosts={oneTimeUnitCosts}
                costForm={costForm}
                setCostForm={setCostForm}
                costError={costError}
                costLoading={costLoading}
                editingCostId={editingCostId}
                unitCostsTotalMonthly={unitCostsTotalMonthly}
                onSubmit={handleUnitCostSubmit}
                onEdit={handleUnitCostEdit}
                onDelete={handleUnitCostDelete}
                onCancel={handleUnitCostCancel}
              />

              <SectionCard
                title="Kaution Vermieter"
                subtitle="Hinterlegung gegenüber dem Vermieter (nicht Mieterkaution)"
              >
                {showLandlordDepositNone ? (
                  <p className="text-[10px] text-[#6b7a9a]">Keine Kaution erfasst</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[#eef2ff]">
                    <div>
                      <p className="text-[10px] text-[#6b7a9a]">Kautionsart</p>
                      <p className="text-[13px] font-medium text-[#eef2ff]">{landlordDepositKindLabel}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#6b7a9a]">Kautionsbetrag</p>
                      <p className="text-[13px] font-medium text-[#eef2ff]">
                        {formatChfOrDash(unit.landlordDepositAmount)}
                      </p>
                    </div>
                    {landlordDepositTypeKey === "insurance" ? (
                      <div>
                        <p className="text-[10px] text-[#6b7a9a]">Jahresprämie</p>
                        <p className="text-[13px] font-medium text-[#eef2ff]">
                          {formatChfOrDash(unit.landlordDepositAnnualPremium)}
                        </p>
                      </div>
                    ) : null}
                  </div>
                )}
              </SectionCard>
            </div>
          </DetailTabPanel>

          <DetailTabPanel tabId="inventory">
            <InventorySection unitId={unitId} rooms={rooms} unitType={safeUnit?.type} />
          </DetailTabPanel>

          <DetailTabPanel tabId="documents">
            <SectionCard
              title="Dokumente"
              subtitle="Dateien zu dieser Unit"
              rightSlot={
                <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center sm:flex-wrap sm:justify-end">
                  <label className="flex items-center gap-2 text-sm text-[#6b7a9a]">
                    <span className="whitespace-nowrap">Kategorie</span>
                    <select
                      value={unitDocCategory}
                      onChange={(e) => setUnitDocCategory(e.target.value)}
                      disabled={unitDocUploading || !unitId}
                      className="text-sm border border-black/10 dark:border-white/[0.1] rounded-[8px] px-2 py-1.5 bg-slate-100 dark:bg-[#111520] dark:border-white/[0.08] text-[#0f172a] dark:text-[#eef2ff] disabled:opacity-50"
                    >
                      <option value="">—</option>
                      <option value="rent_contract">Mietvertrag</option>
                      <option value="insurance">Versicherung</option>
                      <option value="internet">Internet</option>
                      <option value="handover">Übergabe</option>
                      <option value="other">Sonstiges</option>
                    </select>
                  </label>
                  <input
                    ref={unitDocFileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleUnitDocumentSelected}
                  />
                  <button
                    type="button"
                    onClick={handleUnitDocumentPick}
                    disabled={unitDocUploading || !unitId}
                    className="text-sm border border-black/10 dark:border-white/[0.1] hover:bg-slate-100 dark:hover:bg-[#111520] text-[#0f172a] dark:text-[#eef2ff] px-3 py-2 rounded-lg font-medium disabled:opacity-50"
                  >
                    {unitDocUploading ? "Wird hochgeladen …" : "Hochladen"}
                  </button>
                </div>
              }
            >
              {unitDocUploadError ? (
                <p className="text-sm text-[#f87171] mb-2">{unitDocUploadError}</p>
              ) : null}
              {unitDocumentsLoading ? (
                <p className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">Lade Dokumente …</p>
              ) : unitDocumentsError ? (
                <p className="text-sm text-[#f87171]">{unitDocumentsError}</p>
              ) : unitDocuments.length === 0 ? (
                <p className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">Keine Dokumente vorhanden</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-[#0f172a] dark:text-[#eef2ff]">
                    <thead>
                      <tr className="bg-slate-100 dark:bg-[#111520] text-[9px] font-bold uppercase tracking-[.8px] text-[#64748b] dark:text-[#6b7a9a]">
                        <th className="py-2 pr-4 font-medium">Datei</th>
                        <th className="py-2 pr-4 font-medium">Typ</th>
                        <th className="py-2 pr-4 font-medium">Kategorie</th>
                        <th className="py-2 pr-4 font-medium">Datum</th>
                        <th className="py-2 pr-4 font-medium">Von</th>
                        <th className="py-2 pr-4 font-medium">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unitDocuments.map((doc) => (
                        <tr key={String(doc.id)} className="border-b border-black/10 dark:border-white/[0.05]">
                          <td className="py-2 pr-4 font-medium">{doc.file_name || "—"}</td>
                          <td className="py-2 pr-4 text-[#64748b] dark:text-[#6b7a9a]">{formatUnitDocumentType(doc)}</td>
                          <td className="py-2 pr-4 text-[#64748b] dark:text-[#6b7a9a]">
                            {formatUnitDocumentCategoryLabel(doc.category)}
                          </td>
                          <td className="py-2 pr-4 text-[#64748b] dark:text-[#6b7a9a]">
                            {formatUnitDocumentDate(doc.created_at)}
                          </td>
                          <td className="py-2 pr-4 text-[#64748b] dark:text-[#6b7a9a]">
                            {doc.uploaded_by_name != null && doc.uploaded_by_name !== ""
                              ? doc.uploaded_by_name
                              : "—"}
                          </td>
                          <td className="py-2 pr-4">
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                onClick={() => handleOpenUnitDocument(doc.id)}
                                className="text-sky-700 hover:underline font-medium dark:text-sky-400"
                              >
                                Öffnen
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteUnitDocument(doc.id)}
                                className="text-[#64748b] hover:text-[#f87171] hover:underline text-sm dark:text-[#6b7a9a]"
                              >
                                Löschen
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          </DetailTabPanel>

          <DetailTabPanel tabId="tasks">
            <EntityTasksTab
              bare
              filter={{ unit_id: unitId }}
              title="Aufgaben"
            />
          </DetailTabPanel>
          <DetailTabPanel tabId="reports">
            <EntityWorkReportsTab scope="unit" entityId={unitId} />
          </DetailTabPanel>
          <DetailTabPanel tabId="history">
          {/* D3: the unified feed is the primary activity view. The
              detailed change log stays below rather than being removed —
              it renders field-level diffs the feed's curated allowlist
              does not reproduce. One tab, no competing second area. */}
            <div className="mb-4">
              <EntityActivityFeed
                entityType="unit"
                entityId={unitId}
                headingId="unit-activity-heading"
              />
            </div>
            <SectionCard
              title="Verlauf"
              subtitle="Änderungen an dieser Unit (Audit-Log, neueste zuerst)"
            >
              {auditLogLoading ? (
                <p className="text-[10px] text-slate-600 dark:text-[#6b7a9a]">Lade Verlauf …</p>
              ) : auditLogError ? (
                <p className="text-sm text-[#f87171]">{auditLogError}</p>
              ) : auditLogs.length === 0 ? (
                <p className="text-[10px] text-slate-600 dark:text-[#6b7a9a]">Keine Aktivitäten vorhanden</p>
              ) : (
                <AdminAuditTimeline>
                  {auditLogs.map((entry) => {
                    const { summary, changes } = formatAuditLog(entry, {
                      entityType: "unit",
                      resolvers: auditResolvers,
                    });
                    return (
                      <AdminAuditTimelineEntry
                        key={entry.id}
                        summary={summary}
                        changes={changes}
                        createdAt={entry.created_at}
                        actor={auditActorDisplay(entry)}
                        action={entry.action}
                      />
                    );
                  })}
                </AdminAuditTimeline>
              )}
            </SectionCard>
          </DetailTabPanel>
        </DetailTabs>

        <UnitRoomModal
          isOpen={isRoomModalOpen}
          editingRoomId={editingRoomId}
          nextRoomId={nextRoomId}
          unitRooms={unitRooms}
          roomForm={roomForm}
          roomModalDerivedOcc={roomModalDerivedOcc}
          onClose={handleCloseRoomModal}
          onChange={handleRoomChange}
          onSubmit={handleRoomSubmit}
        />
        {/* ROOM-ACT-1b. Retirement, not deletion — the copy says what
            stops being true and what stays, and names no destruction. */}
        <AlertDialog
          open={!!pendingDeactivateRoom}
          onOpenChange={(open) => {
            if (!open && !pendingRoomBusy) setPendingDeactivateRoom(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Zimmer deaktivieren?</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingDeactivateRoom
                  ? `${pendingDeactivateRoom.roomName || "Das Zimmer"} wird aus der aktiven Kapazität entfernt und kann neuen Mietverhältnissen nicht mehr zugewiesen werden. Laufende oder zukünftige Mietverhältnisse verhindern die Deaktivierung. Zimmer und Verlauf bleiben erhalten und lassen sich jederzeit reaktivieren.`
                  : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={pendingRoomBusy}>
                Abbrechen
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmRoomDeactivate}
                disabled={pendingRoomBusy}
              >
                Zimmer deaktivieren
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <UnitAssignModal
          isOpen={assignOpen}
          assignSaving={assignSaving}
          assignListsLoading={assignListsLoading}
          assignForm={assignForm}
          setAssignForm={setAssignForm}
          assignLists={assignLists}
          assignErr={assignErr}
          onClose={() => setAssignOpen(false)}
          onSave={saveAssignModal}
        />
      </div>
    </div>
  );
}

export default AdminUnitDetailPage;
